import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { appUrl } from "@/lib/platform";
import { TopUpLink } from "@/components/CreditsNotice";
import { uploadFileSmart } from "@/lib/chunkedUpload";
import { useAuth } from "@/lib/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  Save, Sparkles, Plus, X, QrCode, Loader2, CheckCircle2, XCircle, ExternalLink, Wand2, Trash2, RotateCw, Undo2,
} from "lucide-react";
import QrModal from "@/components/QrModal";
import SortableImageGrid from "@/components/SortableImageGrid";
import {
  emptySuggestions,
  Section,
  Field,
  ListEditor,
  ReextractButton,
  SuggestionGroup,
  SuggestionFaqGroup,
  SourceFilesEditor,
} from "@/components/product-edit/parts";
import { errorDetail } from "@/lib/utils";

const FIELDS_TEXT = [
  ["name", "Product Name"],
  ["code", "Product Code / SKU"],
  ["category", "Category"],
  ["dimensions", "Dimensions"],
  ["thickness", "Thickness"],
  ["material", "Material"],
];

const PRICE_UNIT_OPTIONS = [
  { value: "piece", label: "Per piece" },
  { value: "box", label: "Per box" },
  { value: "sqft", label: "Per square foot" },
  { value: "sheet", label: "Per sheet" },
  { value: "running_ft", label: "Per running foot" },
  { value: "custom", label: "Custom unit…" },
];

const LISTS = [
  ["available_sizes", "Available Sizes"],
  ["available_colors", "Available Colors"],
  ["applications", "Applications"],
];

export default function AdminProductEdit() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [generatingScenes, setGeneratingScenes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const [reloadTick, setReloadTick] = useState(0);

  const load = async () => {
    const { data } = await api.get(`/products/${id}`);
    setP({
      ...data,
      approved_suggestions: data.approved_suggestions || emptySuggestions(),
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      if (!cancelled) {
        setP({
          ...data,
          approved_suggestions: data.approved_suggestions || emptySuggestions(),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick]);

  // Auto-poll while extraction is still running so the page updates as data comes in.
  useEffect(() => {
    if (!p) return;
    if (p.extraction_status !== "processing" && p.extraction_status !== "pending") return;
    const handle = setInterval(() => setReloadTick((t) => t + 1), 5000);
    return () => clearInterval(handle);
  }, [p?.extraction_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (key, val) => setP((x) => ({ ...x, [key]: val }));
  const updList = (key, idx, val) =>
    setP((x) => ({ ...x, [key]: x[key].map((v, i) => (i === idx ? val : v)) }));
  const addList = (key) => setP((x) => ({ ...x, [key]: [...(x[key] || []), ""] }));
  const remList = (key, idx) =>
    setP((x) => ({ ...x, [key]: x[key].filter((_, i) => i !== idx) }));

  const updSpec = (idx, field, val) =>
    setP((x) => ({
      ...x,
      specifications: x.specifications.map((s, i) =>
        i === idx ? { ...s, [field]: val } : s,
      ),
    }));
  const addSpec = () =>
    setP((x) => ({ ...x, specifications: [...(x.specifications || []), { key: "", value: "" }] }));
  const remSpec = (idx) =>
    setP((x) => ({ ...x, specifications: x.specifications.filter((_, i) => i !== idx) }));

  // suggestion approvals: copy items into approved_suggestions
  const isApproved = (group, value) =>
    (p?.approved_suggestions?.[group] || []).some((v) =>
      typeof v === "string" ? v === value : JSON.stringify(v) === JSON.stringify(value),
    );

  const toggleApprove = (group, value) => {
    setP((x) => {
      const list = x.approved_suggestions[group] || [];
      const exists = list.some((v) =>
        typeof v === "string" ? v === value : JSON.stringify(v) === JSON.stringify(value),
      );
      const next = exists
        ? list.filter((v) =>
            typeof v === "string" ? v !== value : JSON.stringify(v) !== JSON.stringify(value),
          )
        : [...list, value];
      return { ...x, approved_suggestions: { ...x.approved_suggestions, [group]: next } };
    });
  };

  const save = async (extraPatch = {}) => {
    setSaving(true);
    try {
      const payload = {
        name: p.name,
        code: p.code,
        category: p.category,
        description: p.description,
        features: (p.features || []).filter(Boolean),
        specifications: (p.specifications || []).filter((s) => s.key || s.value),
        variants: (p.variants || [])
          .filter((v) => v && v.code)
          .map((v) => ({
            code: v.code,
            finish: v.finish || null,
            length: v.length || null,
            coverage: v.coverage || null,
            weight: v.weight || null,
            thickness: v.thickness || null,
            notes: v.notes || null,
          })),
        dimensions: p.dimensions,
        thickness: p.thickness,
        material: p.material,
        available_sizes: (p.available_sizes || []).filter(Boolean),
        available_colors: (p.available_colors || []).filter(Boolean),
        applications: (p.applications || []).filter(Boolean),
        technical_info: p.technical_info,
        installation_instructions: p.installation_instructions,
        approved_suggestions: p.approved_suggestions,
        prices: (p.prices || [])
          .filter((pr) => pr.unit && pr.value !== "" && pr.value != null)
          .map((pr) => ({
            unit: pr.unit,
            label: pr.label || "",
            value: parseFloat(pr.value) || 0,
            currency: pr.currency || "INR",
            notes: pr.notes || "",
          })),
        ...extraPatch,
      };
      const { data } = await api.patch(`/products/${id}`, payload);
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
      return data;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!p.name || !p.code) {
      alert("Please set Product Name and Product Code before publishing.");
      return;
    }
    setPublishing(true);
    try {
      await save();
      const { data } = await api.post(`/products/${id}/publish`);
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(errorDetail(e) || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const reExtract = async () => {
    setExtracting(true);
    try {
      await api.post(`/products/${id}/extract`);
      await load();
    } catch (e) {
      alert(errorDetail(e) || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const uploadMoreImages = async (files) => {
    try {
      const ids = [];
      for (const f of files) {
        const data = await uploadFileSmart(f, { kind: "image" });
        ids.push(data.id);
      }
      if (!ids.length) return;
      // Attach them to the product on the server (a local-only update was
      // lost on reload).
      const { data } = await api.post(`/products/${id}/attach-files`, { image_ids: ids });
      setP((x) => ({ ...x, images: data.images || x.images }));
    } catch (e) {
      alert(errorDetail(e) || "Could not add the images");
    }
  };

  const removeImage = async (fileId) => {
    if (!window.confirm("Remove this image from the product?")) return;
    try {
      await api.delete(`/products/${id}/images/${fileId}`);
      setP((x) => ({
        ...x,
        images: (x.images || []).filter((i) => i.id !== fileId),
      }));
    } catch (e) {
      alert(errorDetail(e) || "Failed to remove image");
    }
  };

  const generateScenes = async () => {
    if (!p.name) {
      alert("Please save a product name first — it's used to generate the scenes.");
      return;
    }
    setGeneratingScenes(true);
    try {
      const { data } = await api.post(`/products/${id}/generate-scenes?count=1`);
      if (data?.status === "generating") {
        // Async — start polling for completion. The backend appends images and flips
        // scene_status back to "idle" (or "failed" with a reason) when done.
        setP((x) => ({ ...x, scene_status: "generating", scene_error: null }));
      } else if (data?.images) {
        // Legacy synchronous response — pre-async backend.
        setP((x) => ({ ...x, images: data.images }));
      }
    } catch (e) {
      const detail = errorDetail(e) || e?.message || "Scene generation failed";
      alert(detail);
      setGeneratingScenes(false);
    }
  };

  // Poll product while scene generation is in progress. Stops when scene_status
  // is no longer "generating".
  useEffect(() => {
    if (p?.scene_status !== "generating") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/products/${id}`);
        if (cancelled) return;
        setP((x) => ({
          ...data,
          approved_suggestions:
            data.approved_suggestions || x?.approved_suggestions || emptySuggestions(),
        }));
        if (data.scene_status !== "generating") {
          setGeneratingScenes(false);
          if (data.scene_status === "failed" && data.scene_error) {
            alert(data.scene_error);
          }
        }
      } catch {
        // ignore transient errors; keep polling
      }
    };
    const handle = setInterval(tick, 5000);
    // Also poll once immediately
    tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.scene_status, id]);

  // Poll product while enrichment (page snapshots + Perplexity research) is running.
  // Stops as soon as enrichment_status flips to "done" or "failed". This way the
  // research notes and reference sheets appear automatically without a manual refresh.
  useEffect(() => {
    if (p?.enrichment_status !== "running") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/products/${id}`);
        if (cancelled) return;
        setP((x) => ({
          ...data,
          approved_suggestions:
            data.approved_suggestions || x?.approved_suggestions || emptySuggestions(),
        }));
      } catch {
        // ignore transient errors; keep polling
      }
    };
    const handle = setInterval(tick, 4000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.enrichment_status, id]);

  const consolidateSpecs = async () => {
    if (!window.confirm(
      "Consolidate the specifications table?\n\nThis groups any 'GL 11 Length', 'GL 11 Weight'… style rows into a single Profile Variants table. " +
      "Global specs (Material Grade, Warranty, Tolerance…) stay as normal rows. " +
      "No AI is re-run, so this is free and instant.",
    )) return;
    try {
      const { data } = await api.post(`/products/${id}/consolidate-specs`);
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(errorDetail(e) || "Consolidation failed");
    }
  };

  // Per-field re-extraction: AI re-reads ONLY the named field from the source PDF and
  // updates it in place. Used by the small "↻" button next to each field section.
  const [reextracting, setReextracting] = useState(null); // field name currently re-extracting
  const reextractField = async (field, friendly) => {
    if (
      !window.confirm(
        `Re-extract "${friendly}" from the original PDF?\n\nThis will REPLACE the current value with what the AI reads fresh from the source files. Other fields are not touched. You can Undo if the new value is worse.`,
      )
    ) return;
    setReextracting(field);
    try {
      const { data } = await api.post(`/products/${id}/re-extract-field`, { field });
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(errorDetail(e) || `Failed to re-extract ${field}`);
    } finally {
      setReextracting(null);
    }
  };

  const undoField = async (field, friendly) => {
    if (
      !window.confirm(
        `Undo last AI re-extraction of "${friendly}"?\n\nThe value will be restored to what it was before you clicked Re-extract.`,
      )
    ) return;
    setReextracting(field);
    try {
      const { data } = await api.post(`/products/${id}/undo-field`, { field });
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(errorDetail(e) || `Failed to undo ${field}`);
    } finally {
      setReextracting(null);
    }
  };

  const deleteProduct = async () => {
    const label = p.name || p.code || "this product";
    if (
      !window.confirm(
        `Delete "${label}"? This permanently removes the product, its AI suggestions and reference sheets. This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/products/${id}`);
      nav("/admin/products");
    } catch (e) {
      alert(errorDetail(e) || "Failed to delete product");
      setDeleting(false);
    }
  };

  if (!p) {
    return (
      <AdminLayout>
        <div className="flex items-center gap-2 text-sm text-heda-mute">
          <Loader2 size={18} className="animate-spin text-heda-gold" /> Loading...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 md:mb-8">
        <div className="min-w-0">
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
              p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-heda-cream text-heda-mute"
            }`}
          >
            {p.status === "published" ? "Published Product" : "Draft Product"}
          </span>
          <h1 className="mt-2 break-words font-display text-2xl font-bold text-heda-ink md:text-[30px]">
            {p.name || "Untitled product"}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-heda-mute">
            <span>CODE · {p.code || "—"}</span>
            <span>·</span>
            <span>AI: {p.extraction_status}{p.extraction_stage ? ` (${p.extraction_stage.replace(/_/g, " ")})` : ""}</span>
            {p.enrichment_status === "running" && (
              <>
                <span>·</span>
                <span
                  className="inline-flex items-center gap-1.5 text-heda-gold"
                  data-testid="enrichment-pill"
                >
                  <Loader2 size={11} className="animate-spin" />
                  Enriching (page snapshots &amp; web research)
                </span>
              </>
            )}
          </div>
          {p.extraction_status === "failed" && p.extraction_error && (
            <div
              className="mt-3 max-w-xl rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
              data-testid="extraction-error-banner"
            >
              <div className="font-semibold mb-1">AI extraction failed</div>
              <div className="tabular-nums text-[11px] leading-relaxed break-words">
                {p.extraction_error}
                <TopUpLink text={p.extraction_error} />
              </div>
              <div className="text-[11px] mt-2 opacity-80">
                Click <strong>Re-run AI</strong> on the right to try again.
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === "owner" && (
            <button
              onClick={deleteProduct}
              disabled={deleting}
              className="hed-btn-line text-red-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
              data-testid="delete-product-button"
              title="Delete this product"
            >
              {deleting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
          )}
          <button
            onClick={reExtract}
            disabled={extracting}
            className="hed-btn-line"
            data-testid="re-extract-button"
          >
            {extracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Re-run AI
          </button>
          <button
            onClick={() => save()}
            disabled={saving}
            className="heda-btn py-2.5"
            data-testid="save-product-button"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save draft
          </button>
          {p.status === "published" ? (
            <>
              <button
                onClick={() => setQrOpen(true)}
                className="hed-btn-line"
                data-testid="open-qr-button"
              >
                <QrCode size={14} /> QR
              </button>
              <a
                href={appUrl(`/product/${p.code}`)}
                target="_blank"
                rel="noreferrer"
                className="hed-btn-line"
                data-testid="open-public-link"
              >
                Open page <ExternalLink size={14} />
              </a>
            </>
          ) : (
            <button
              onClick={publish}
              disabled={publishing}
              className="heda-btn-primary px-5 py-2.5 text-sm"
              data-testid="publish-product-button"
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Publish
            </button>
          )}
        </div>
      </div>

      {/* Main grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Form */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Section title="Identity">
            <div className="grid sm:grid-cols-2 gap-5">
              {FIELDS_TEXT.map(([k, label]) => (
                <Field key={k} label={label}>
                  <input
                    data-testid={`field-${k}`}
                    className="heda-input"
                    value={p[k] || ""}
                    onChange={(e) => upd(k, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </Section>

          <Section
            title="Description"
            action={
              <ReextractButton
                field="description"
                label="Description"
                busy={reextracting === "description"}
                onClick={() => reextractField("description", "Description")}
                canUndo={Boolean(p.field_history?.description !== undefined)}
                onUndo={() => undoField("description", "Description")}
              />
            }
          >
            <textarea
              data-testid="field-description"
              rows={4}
              className="heda-input resize-none"
              value={p.description || ""}
              onChange={(e) => upd("description", e.target.value)}
            />
          </Section>

          <Section title="Pricing">
            <p className="mb-3 text-[13px] text-heda-sub">
              Add one or more price points. Each is shown on the product page with its unit.
            </p>
            <div className="space-y-2">
              {(p.prices || []).map((pr, idx) => {
                const isCustom = !PRICE_UNIT_OPTIONS.find((o) => o.value === pr.unit && o.value !== "custom");
                return (
                  <div
                    key={idx}
                    data-testid={`price-row-${idx}`}
                    className="grid grid-cols-12 items-center gap-2 rounded-xl border border-heda-line bg-heda-cream/50 p-2.5"
                  >
                    <div className="col-span-12 sm:col-span-5">
                      <select
                        value={isCustom ? "custom" : pr.unit || "piece"}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "custom") {
                            setP((x) => ({
                              ...x,
                              prices: x.prices.map((q, i) =>
                                i === idx ? { ...q, unit: q.unit || "", _custom: true } : q,
                              ),
                            }));
                          } else {
                            setP((x) => ({
                              ...x,
                              prices: x.prices.map((q, i) =>
                                i === idx ? { ...q, unit: v, _custom: false } : q,
                              ),
                            }));
                          }
                        }}
                        className="heda-input bg-white py-2"
                      >
                        {PRICE_UNIT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      {(isCustom || pr._custom) && (
                        <input
                          placeholder="Custom unit (e.g. per metre)"
                          value={pr.unit && !PRICE_UNIT_OPTIONS.find((o) => o.value === pr.unit) ? pr.unit : ""}
                          onChange={(e) =>
                            setP((x) => ({
                              ...x,
                              prices: x.prices.map((q, i) =>
                                i === idx ? { ...q, unit: e.target.value } : q,
                              ),
                            }))
                          }
                          className="heda-input mt-2 bg-white py-2"
                        />
                      )}
                    </div>
                    <div className="col-span-5 flex items-center gap-1.5 rounded-xl border border-heda-line bg-white px-3 focus-within:border-heda-gold sm:col-span-3">
                      <span className="text-sm text-heda-mute">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={pr.value ?? ""}
                        onChange={(e) =>
                          setP((x) => ({
                            ...x,
                            prices: x.prices.map((q, i) =>
                              i === idx ? { ...q, value: e.target.value } : q,
                            ),
                          }))
                        }
                        className="w-full min-w-0 flex-1 bg-transparent py-2 text-[15px] outline-none"
                      />
                    </div>
                    <input
                      placeholder="Note (optional)"
                      value={pr.notes || ""}
                      onChange={(e) =>
                        setP((x) => ({
                          ...x,
                          prices: x.prices.map((q, i) =>
                            i === idx ? { ...q, notes: e.target.value } : q,
                          ),
                        }))
                      }
                      className="heda-input col-span-5 bg-white py-2 sm:col-span-3"
                    />
                    <button
                      onClick={() =>
                        setP((x) => ({ ...x, prices: x.prices.filter((_, i) => i !== idx) }))
                      }
                      className="hed-icon-btn col-span-2 justify-self-center hover:bg-red-50 hover:text-red-700 sm:col-span-1"
                      data-testid={`price-remove-${idx}`}
                    >
                      <X size={15} />
                    </button>
                  </div>
                );
              })}
              <button
                onClick={() =>
                  setP((x) => ({
                    ...x,
                    prices: [
                      ...(x.prices || []),
                      { unit: "piece", value: "", currency: "INR", notes: "" },
                    ],
                  }))
                }
                className="heda-pill mt-2"
                data-testid="add-price-button"
              >
                <Plus size={12} /> Add price
              </button>
            </div>
          </Section>

          <Section
            title="Features"
            action={
              <ReextractButton
                field="features"
                label="Features"
                busy={reextracting === "features"}
                onClick={() => reextractField("features", "Features")}
                canUndo={Boolean(p.field_history?.features !== undefined)}
                onUndo={() => undoField("features", "Features")}
              />
            }
          >
            <ListEditor
              items={p.features || []}
              onChange={(idx, v) => updList("features", idx, v)}
              onAdd={() => addList("features")}
              onRemove={(idx) => remList("features", idx)}
              testId="feature"
              placeholder="A short feature highlight"
            />
          </Section>

          <Section
            title="Specifications"
            action={
              <ReextractButton
                field="specifications"
                label="Specifications"
                busy={reextracting === "specifications"}
                onClick={() => reextractField("specifications", "Specifications")}
                canUndo={Boolean(p.field_history?.specifications !== undefined)}
                onUndo={() => undoField("specifications", "Specifications")}
              />
            }
          >
            <div className="space-y-2">
              {(p.specifications || []).map((s, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`spec-row-${idx}`}>
                  <input
                    placeholder="Key"
                    value={s.key || ""}
                    onChange={(e) => updSpec(idx, "key", e.target.value)}
                    className="heda-input w-2/5 min-w-0 py-2"
                  />
                  <input
                    placeholder="Value"
                    value={s.value || ""}
                    onChange={(e) => updSpec(idx, "value", e.target.value)}
                    className="heda-input min-w-0 flex-1 py-2"
                  />
                  <button
                    onClick={() => remSpec(idx)}
                    className="hed-icon-btn hover:bg-red-50 hover:text-red-700"
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
              <button
                onClick={addSpec}
                className="heda-pill mt-2"
                data-testid="add-spec-button"
              >
                <Plus size={12} /> Add specification
              </button>
            </div>
          </Section>

          <Section title="Profile / Variants">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="min-w-[200px] flex-1 text-[13px] text-heda-sub">
                When the brochure lists several profile variants (e.g. GL 11, GL 12, ...), each row here
                becomes one row in the public Variants table. Leave fields blank if not applicable.
              </p>
              <button
                onClick={consolidateSpecs}
                className="heda-pill"
                data-testid="consolidate-specs-button"
                title="Group 'GL 11 Length, GL 11 Weight, …' rows into a single Variants table"
              >
                <Sparkles size={12} className="text-heda-gold" /> Consolidate from specs
              </button>
            </div>
            {(p.variants || []).length === 0 ? (
              <div className="mb-3 text-[13px] text-heda-mute">
                No variants — most products won&rsquo;t need this. Add one only if there are multiple
                profile codes/finishes that share the same product.
              </div>
            ) : (
              <div className="-mx-5 overflow-x-auto px-5">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-heda-mute">
                      <th className="pb-2 pr-2 font-semibold">Code</th>
                      <th className="pb-2 pr-2 font-semibold">Finish</th>
                      <th className="pb-2 pr-2 font-semibold">Length</th>
                      <th className="pb-2 pr-2 font-semibold">Coverage</th>
                      <th className="pb-2 pr-2 font-semibold">Weight</th>
                      <th className="pb-2 pr-2 font-semibold">Thickness</th>
                      <th className="w-10 pb-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(p.variants || []).map((v, idx) => (
                      <tr key={idx} data-testid={`variant-row-${idx}`} className="border-t border-heda-line">
                        {["code", "finish", "length", "coverage", "weight", "thickness"].map((field) => (
                          <td key={field} className="py-1.5 pr-2">
                            <input
                              value={v[field] || ""}
                              placeholder={field === "code" ? "GL 11" : "—"}
                              data-testid={`variant-${field}-${idx}`}
                              onChange={(e) =>
                                setP((x) => ({
                                  ...x,
                                  variants: x.variants.map((q, i) =>
                                    i === idx ? { ...q, [field]: e.target.value } : q,
                                  ),
                                }))
                              }
                              className="heda-input px-2.5 py-1.5"
                            />
                          </td>
                        ))}
                        <td className="py-1.5">
                          <button
                            onClick={() =>
                              setP((x) => ({
                                ...x,
                                variants: x.variants.filter((_, i) => i !== idx),
                              }))
                            }
                            className="hed-icon-btn hover:bg-red-50 hover:text-red-700"
                            data-testid={`variant-remove-${idx}`}
                          >
                            <X size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              onClick={() =>
                setP((x) => ({
                  ...x,
                  variants: [...(x.variants || []), { code: "" }],
                }))
              }
              className="heda-pill mt-3"
              data-testid="add-variant-button"
            >
              <Plus size={12} /> Add variant
            </button>
          </Section>

          {LISTS.map(([k, label]) => (
            <Section
              key={k}
              title={label}
              action={
                <ReextractButton
                  field={k}
                  label={label}
                  busy={reextracting === k}
                  onClick={() => reextractField(k, label)}
                  canUndo={Boolean(p.field_history?.[k] !== undefined)}
                  onUndo={() => undoField(k, label)}
                />
              }
            >
              <ListEditor
                items={p[k] || []}
                onChange={(idx, v) => updList(k, idx, v)}
                onAdd={() => addList(k)}
                onRemove={(idx) => remList(k, idx)}
                testId={k}
                placeholder={`Add ${label.toLowerCase()}`}
              />
            </Section>
          ))}

          <Section
            title="Technical information"
            action={
              <ReextractButton
                field="technical_info"
                label="Technical information"
                busy={reextracting === "technical_info"}
                onClick={() => reextractField("technical_info", "Technical information")}
                canUndo={Boolean(p.field_history?.technical_info !== undefined)}
                onUndo={() => undoField("technical_info", "Technical information")}
              />
            }
          >
            <textarea
              rows={3}
              data-testid="field-technical-info"
              className="heda-input resize-none"
              value={p.technical_info || ""}
              onChange={(e) => upd("technical_info", e.target.value)}
            />
          </Section>
          <Section
            title="Installation instructions"
            action={
              <ReextractButton
                field="installation_instructions"
                label="Installation instructions"
                busy={reextracting === "installation_instructions"}
                onClick={() => reextractField("installation_instructions", "Installation instructions")}
                canUndo={Boolean(p.field_history?.installation_instructions !== undefined)}
                onUndo={() => undoField("installation_instructions", "Installation instructions")}
              />
            }
          >
            <textarea
              rows={3}
              data-testid="field-installation"
              className="heda-input resize-none"
              value={p.installation_instructions || ""}
              onChange={(e) => upd("installation_instructions", e.target.value)}
            />
          </Section>

          <Section title="Web research">
            {p.research_notes ? (
              <div data-testid="admin-research-block">
                <p className="mb-3 text-[13px] text-heda-sub">
                  Researched on the web — also shown on the public product page in &ldquo;Beyond the brochure&rdquo;
                  and fed to the AI chatbot for richer answers.
                </p>
                <div className="max-h-72 overflow-y-auto whitespace-pre-line rounded-xl border border-heda-line bg-heda-cream/50 p-3 text-sm leading-relaxed text-heda-ink/90">
                  {p.research_notes}
                </div>
                {p.research_sources?.length > 0 && (
                  <div className="mt-3">
                    <div className="hed-overline mb-1.5">
                      Sources ({p.research_sources.length})
                    </div>
                    <ul className="space-y-1">
                      {p.research_sources.slice(0, 6).map((u, i) => (
                        <li key={i} className="text-xs break-all">
                          <a
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="text-heda-gold underline underline-offset-2 hover:text-heda-ink"
                          >
                            {u}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : p.research_error ? (
              <div
                className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
                data-testid="research-error-banner"
              >
                <div className="font-semibold mb-1">Web research failed</div>
                <div className="tabular-nums text-[11px] leading-relaxed break-words">
                  {p.research_error}
                </div>
                <div className="text-[11px] mt-2 opacity-80">
                  Click <strong>Re-run AI</strong> to try again. If it keeps failing, contact support.
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-heda-mute">
                {p.extraction_status === "completed"
                  ? "Web research returned no notes for this product. Click Re-run AI to try again."
                  : "Will appear here once AI extraction + web research complete."}
              </div>
            )}
          </Section>
        </div>

        {/* Right: Images + AI Suggestions */}
        <div className="min-w-0 space-y-6">
          <Section title="Product images">
            <SortableImageGrid
              productId={id}
              images={p.images || []}
              onImagesChange={(imgs) => setP((x) => ({ ...x, images: imgs }))}
              onAddFiles={uploadMoreImages}
            />

            <button
              onClick={generateScenes}
              disabled={generatingScenes || p.scene_status === "generating"}
              data-testid="generate-scenes-button"
              className="heda-btn mt-4 w-full"
            >
              {generatingScenes || p.scene_status === "generating" ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Rendering scenes in background…
                </>
              ) : (
                <>
                  <Wand2 size={14} />
                  Generate 1 AI scene render
                </>
              )}
            </button>
            {p.scene_status === "generating" && (
              <div
                className="mt-3 rounded-xl border border-heda-gold/40 bg-heda-gold-tint p-3 text-xs text-heda-ink"
                data-testid="scene-generating-banner"
              >
                <div className="font-semibold mb-0.5">AI scene rendering in progress…</div>
                <div className="leading-relaxed opacity-90">
                  Each image takes about 15 seconds. The new renders will appear automatically
                  once ready — you can leave this page and come back.
                </div>
              </div>
            )}
            {p.scene_status === "failed" && p.scene_error && (
              <div
                className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
                data-testid="scene-error-banner"
              >
                <div className="font-semibold mb-1">Scene generation failed</div>
                <div className="tabular-nums text-[11px] leading-relaxed break-words">
                  {p.scene_error}
                </div>
                <div className="text-[11px] mt-2 opacity-80">
                  Click <strong>Generate 1 AI scene render</strong> to try again.
                </div>
              </div>
            )}
            <p className="mt-2 text-[11.5px] leading-relaxed text-heda-mute">
              AI renders the product applied in living room, wardrobe, kitchen back-panel and door scenes — photorealistic.
            </p>
          </Section>

          <Section title="Source PDFs">
            <p className="mb-3 text-[13px] text-heda-sub">
              The original brochures / catalogs Hedaply AI reads. You can add more PDFs
              anytime — the QR code stays the same. After attaching, click <strong>Re-run AI</strong>
              to merge new information into this product.
            </p>
            <SourceFilesEditor
              productId={id}
              files={p.files || []}
              onChange={(updated) =>
                setP((x) => ({ ...x, files: updated.files, images: updated.images }))
              }
            />
          </Section>

          <Section title="Reference Sheets">
            <p className="mb-3 text-[13px] text-heda-sub">
              Snapshot pages from your PDF (color charts, specs, applications, installation).
              Toggle which ones to show on the public product page.
            </p>
            {(p.reference_sheets || []).length === 0 ? (
              <div className="text-[13px] text-heda-mute">
                None yet — run AI extraction on a PDF and Hedaply will auto-snapshot the relevant pages.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(p.reference_sheets || []).map((s, idx) => (
                  <div
                    key={s.id}
                    className="overflow-hidden rounded-xl border border-heda-line bg-white"
                    data-testid={`ref-sheet-${idx}`}
                  >
                    <div className="aspect-[3/4] bg-heda-cream overflow-hidden">
                      <img
                        src={fileUrl(s.storage_path)}
                        alt={s.title || s.label}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2.5">
                      <div className="hed-overline text-[10px]">
                        {s.label.replace("_", " ")}
                      </div>
                      <div className="mt-0.5 truncate text-xs font-semibold text-heda-ink">{s.title}</div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-heda-sub">
                          <input
                            type="checkbox"
                            checked={s.include_public}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setP((x) => ({
                                ...x,
                                reference_sheets: x.reference_sheets.map((q) =>
                                  q.id === s.id ? { ...q, include_public: checked } : q,
                                ),
                              }));
                              try {
                                await api.patch(`/products/${id}/sheets/${s.id}`, {
                                  include_public: checked,
                                });
                              } catch (err) {
                                /* ignore */
                              }
                            }}
                            className="accent-heda-gold"
                            data-testid={`ref-sheet-toggle-${idx}`}
                          />
                          Show
                        </label>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Remove this reference sheet?")) return;
                            try {
                              await api.delete(`/products/${id}/sheets/${s.id}`);
                              setP((x) => ({
                                ...x,
                                reference_sheets: x.reference_sheets.filter((q) => q.id !== s.id),
                              }));
                            } catch (err) {
                              alert("Failed to remove");
                            }
                          }}
                          className="rounded-full p-1.5 text-heda-mute hover:bg-red-50 hover:text-red-700"
                          data-testid={`ref-sheet-remove-${idx}`}
                        >
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <div className="rounded-2xl border border-heda-gold/30 bg-heda-gold-tint/50 p-5">
            <h3 className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <Sparkles size={16} className="text-heda-gold" /> AI Suggested Information
            </h3>
            <p className="mb-2 mt-1 text-[13px] text-heda-sub">
              Approve to include on the product page & in the AI assistant. Nothing is saved permanently until you approve.
            </p>

            <SuggestionGroup
              title="Recommended applications"
              items={p.suggestions?.recommended_applications || []}
              isApproved={(v) => isApproved("recommended_applications", v)}
              toggle={(v) => toggleApprove("recommended_applications", v)}
              testIdPrefix="sugg-rec-app"
            />
            <SuggestionGroup
              title="Maintenance instructions"
              items={p.suggestions?.maintenance_instructions || []}
              isApproved={(v) => isApproved("maintenance_instructions", v)}
              toggle={(v) => toggleApprove("maintenance_instructions", v)}
              testIdPrefix="sugg-maint"
            />
            <SuggestionGroup
              title="Use cases"
              items={p.suggestions?.use_cases || []}
              isApproved={(v) => isApproved("use_cases", v)}
              toggle={(v) => toggleApprove("use_cases", v)}
              testIdPrefix="sugg-use"
            />
            <SuggestionGroup
              title="Installation recommendations"
              items={p.suggestions?.installation_recommendations || []}
              isApproved={(v) => isApproved("installation_recommendations", v)}
              toggle={(v) => toggleApprove("installation_recommendations", v)}
              testIdPrefix="sugg-install"
            />
            <SuggestionFaqGroup
              items={p.suggestions?.faqs || []}
              isApproved={(v) => isApproved("faqs", v)}
              toggle={(v) => toggleApprove("faqs", v)}
            />
          </div>
        </div>
      </div>

      {qrOpen && <QrModal product={p} onClose={() => setQrOpen(false)} />}
    </AdminLayout>
  );
}

